import { Alert, Grid, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { FormSelectInputDisplay } from '../../form-field-components/new-and-edit/form-select-input'
import { IReportPeriod } from '../../../../shared/services/report-period/report-period.model'

/**
 * Step 1: the reporting period the disclosure covers. Comparatives are not picked here: the previous
 * year and base year columns are derived automatically from the organisation's period history, the
 * same way the GHG report derives them. The period is fixed once the report has been generated, so
 * this step is read-only after generation rather than silently rewriting a reviewed document's scope.
 */
export const SrsStepSetup: FunctionComponent<{
  reportPeriods: IReportPeriod[]
  reportPeriodId: string
  locked: boolean
  onChange: (value: string) => void
}> = ({ reportPeriods, reportPeriodId, locked, onChange }) => {
  const periodOptions = reportPeriods.map((period) => ({
    id: period.id,
    name: period.friendlyName || String(period.calendarYear),
  }))

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>Reporting period</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
        A UK SRS S2 climate disclosure covers one reporting period. It follows the four pillars of the
        standard: governance, strategy, risk management, and metrics and targets.
      </Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        Previous year and base year comparatives are derived automatically from your reporting
        history, so the emissions table always reconciles with your other reports for the same
        period.
      </Typography>

      {locked && (
        <Alert severity='info' sx={{ mb: 3 }}>
          This report has already been generated, so the reporting period is fixed. Create a new
          report to disclose a different period.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormSelectInputDisplay
            isEditPage={false}
            disabled={locked}
            title='Reporting period'
            name='reportPeriodId'
            value={reportPeriodId}
            onChange={(event: any) => onChange(event.target.value)}
            array={periodOptions}
            valueKey='id'
            labelKey='name'
            forceOutlined
            isTopRow
            isBottomRow
            tooltip='The period the disclosure covers. Comparative years are derived automatically.'
          />
        </Grid>
      </Grid>
    </>
  )
}
